import type { ReactNode } from 'react'

/**
 * A small Markdown renderer for assistant answers.
 *
 * Deliberately a subset, not CommonMark: what a chat model actually emits in
 * an answer -- headings, bullet and numbered lists, bold, italic, inline code,
 * links -- and nothing else. The system prompt names the same subset, so the
 * two agree.
 *
 * It returns React elements rather than an HTML string, which is the whole
 * reason not to reach for a library plus a sanitiser: there is no
 * `dangerouslySetInnerHTML` anywhere, so a pantry item called
 * `<img onerror=...>` renders as those characters and can never execute.
 */

/** bold | bold | italic | code | link */
const INLINE =
  /\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\n]+)\*|`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]+)\)/g

/**
 * Only http(s) links become anchors. A `javascript:` href would be the one way
 * model output could execute, so anything else stays plain text.
 */
function safeHref(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null
  } catch {
    return null
  }
}

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let n = 0
  INLINE.lastIndex = 0

  for (let m = INLINE.exec(text); m !== null; m = INLINE.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const id = `${key}-${n++}`

    if (m[1] !== undefined || m[2] !== undefined) {
      out.push(<strong key={id}>{m[1] ?? m[2]}</strong>)
    } else if (m[3] !== undefined) {
      out.push(<em key={id}>{m[3]}</em>)
    } else if (m[4] !== undefined) {
      out.push(<code key={id}>{m[4]}</code>)
    } else if (m[5] !== undefined && m[6] !== undefined) {
      const href = safeHref(m[6])
      out.push(
        href ? (
          <a key={id} href={href} target="_blank" rel="noopener noreferrer">
            {m[5]}
          </a>
        ) : (
          m[0]
        ),
      )
    }
    last = m.index + m[0].length
  }

  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Lines inside one paragraph keep their breaks; models mean them. */
function paragraph(lines: string[], key: string): ReactNode {
  return (
    <p key={key}>
      {lines.flatMap((line, i) => [
        ...(i > 0 ? [<br key={`${key}-br-${i}`} />] : []),
        ...inline(line, `${key}-${i}`),
      ])}
    </p>
  )
}

interface Item {
  indent: number
  text: string
  ordered: boolean
}

interface Node {
  text: string
  ordered: boolean
  children: Node[]
}

/**
 * Group flat, indented items into a tree.
 *
 * Bullets and numbers are collected together rather than one kind at a time,
 * because a numbered step with indented bullets under it is the single most
 * common shape a model produces -- and gathering only one kind turns those
 * children into a sibling list instead of a nested one.
 */
function buildTree(items: Item[], start: number, indent: number): { nodes: Node[]; next: number } {
  const nodes: Node[] = []
  let i = start

  while (i < items.length && items[i]!.indent >= indent) {
    const item = items[i]!

    // Indented further than anything opened it: treat it as a child of the
    // previous item rather than dropping it.
    if (item.indent > indent) {
      const sub = buildTree(items, i, item.indent)
      if (nodes.length > 0) nodes[nodes.length - 1]!.children.push(...sub.nodes)
      else nodes.push(...sub.nodes)
      i = sub.next
      continue
    }

    const node: Node = { text: item.text, ordered: item.ordered, children: [] }
    nodes.push(node)
    i++

    if (i < items.length && items[i]!.indent > indent) {
      const sub = buildTree(items, i, items[i]!.indent)
      node.children.push(...sub.nodes)
      i = sub.next
    }
  }

  return { nodes, next: i }
}

/** Render a level, starting a new list whenever bullets and numbers alternate. */
function renderNodes(nodes: Node[], key: string): ReactNode[] {
  const out: ReactNode[] = []
  let i = 0

  while (i < nodes.length) {
    const ordered = nodes[i]!.ordered
    const run: Node[] = []
    while (i < nodes.length && nodes[i]!.ordered === ordered) {
      run.push(nodes[i]!)
      i++
    }
    const id = `${key}-${out.length}`
    const children = run.map((node, n) => (
      <li key={`${id}-${n}`}>
        {inline(node.text, `${id}-${n}`)}
        {node.children.length > 0 && renderNodes(node.children, `${id}-${n}-n`)}
      </li>
    ))
    out.push(ordered ? <ol key={id}>{children}</ol> : <ul key={id}>{children}</ul>)
  }

  return out
}

const HEADING = /^(#{1,4})\s+(.*)$/
const BULLET = /^(\s*)[-*+]\s+(.*)$/
const NUMBERED = /^(\s*)\d+[.)]\s+(.*)$/
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/

export function renderMarkdown(source: string): ReactNode[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')

  /*
   * Headings are normalised, not mapped one-to-one. An answer is embedded in a
   * page that already has an <h1>, and models are inconsistent about whether
   * their top heading is `#` or `###`. So whatever the shallowest heading in
   * *this* answer is becomes <h3>, and deeper ones step down from there --
   * hierarchy preserved, and a `###`-only answer is not rendered as a caption.
   */
  let top = 5
  for (const line of lines) {
    const m = HEADING.exec(line)
    if (m) top = Math.min(top, m[1]!.length)
  }

  const blocks: ReactNode[] = []
  let para: string[] = []
  let n = 0

  const flush = () => {
    if (para.length > 0) {
      blocks.push(paragraph(para, `p${n++}`))
      para = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!

    if (line.trim() === '') {
      flush()
      continue
    }

    // A rule has to be tested before a bullet: "---" matches both.
    if (RULE.test(line)) {
      flush()
      blocks.push(<hr key={`h${n++}`} />)
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      flush()
      const depth = Math.min(heading[1]!.length - top, 3)
      const Tag = (['h3', 'h4', 'h5', 'h6'] as const)[depth]!
      blocks.push(<Tag key={`t${n++}`}>{inline(heading[2]!, `t${n}`)}</Tag>)
      continue
    }

    if (BULLET.test(line) || NUMBERED.test(line)) {
      flush()
      const items: Item[] = []
      let j = i
      for (; j < lines.length; j++) {
        const bullet = BULLET.exec(lines[j]!)
        const numbered = bullet ? null : NUMBERED.exec(lines[j]!)
        const m = bullet ?? numbered
        if (!m || RULE.test(lines[j]!)) break
        items.push({ indent: m[1]!.length, text: m[2]!, ordered: numbered !== null })
      }
      const key = `l${n++}`
      blocks.push(
        <div className="md-list" key={key}>
          {renderNodes(buildTree(items, 0, items[0]!.indent).nodes, key)}
        </div>,
      )
      i = j - 1
      continue
    }

    para.push(line)
  }

  flush()
  return blocks
}

/** Assistant answers, formatted. */
export function Markdown({ text }: { text: string }) {
  return <div className="md">{renderMarkdown(text)}</div>
}
